import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronUp } from 'lucide-react';

const SCROLL_THRESHOLD = 320;
const VISIBILITY_DEBOUNCE = 100;

export default function BackToTop() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        const currentScroll = window.scrollY || document.documentElement.scrollTop;
        setIsVisible(currentScroll > SCROLL_THRESHOLD);
      }, VISIBILITY_DEBOUNCE);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          type="button"
          onClick={scrollToTop}
          aria-label="Back to top"
          initial={{ opacity: 0, y: 24, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.9 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="fixed bottom-8 right-6 z-50 rounded-full bg-gradient-to-r from-launch-blue via-launch-purple to-launch-orange p-[1px] shadow-lg shadow-launch-blue/40 backdrop-blur"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-night-900/95 text-offwhite transition-all duration-200 hover:bg-night-800/95">
            <ChevronUp className="h-6 w-6" />
          </div>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
